import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, Divider, Typography, FormControlLabel, Switch, CardActions, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import { pick, omit } from "lodash";
import * as Joi from "joi";
import { runJoiValidate } from "src/_utils";
import i18n from "src/i18n";

const MOTTASL_SCHEMA = Joi.object({
    active: Joi.boolean()
        .required(),
    apiKey: Joi.string()
        .when('active', {
            is: true,
            then: Joi.string()
                .required()
                .empty('')
                .min(3)
                .label(i18n.t("API Key"))
                .messages({
                    'any.required': `{#label} ${i18n.t("validationErrors.isRequired")}`,
                    'string.empty': `{#label} ${i18n.t("validationErrors.isEmpty")}`,
                    'string.min': `{#label} ${i18n.t("validationErrors.minChar", { length: 3 })}`,
                }),
            otherwise: Joi.string().optional().allow('')
        }),
    token: Joi.string()
        .when('active', {
            is: true,
            then: Joi.string()
                .required()
                .empty('')
                .min(3)
                .label(i18n.t("Token"))
                .messages({
                    'any.required': `{#label} ${i18n.t("validationErrors.isRequired")}`,
                    'string.empty': `{#label} ${i18n.t("validationErrors.isEmpty")}`,
                    'string.min': `{#label} ${i18n.t("validationErrors.minChar", { length: 3 })}`,
                }),
            otherwise: Joi.string().optional().allow('')
        })
});



export default function MottaslCard({
    data,
    onConfirm,
    loading,
    error,
    isSuccess,
}) {
    const { t } = useTranslation();

    const [mottaslSetting, setMottaslSetting] = useState({
        active: false,
        apiKey: '',
        token: '',
    })

    const [validationErrors, setValidationErrors] = useState(null)

    const handleChange = ({ target: { name, value } }) => {
        setMottaslSetting(prev => ({ ...prev, [name]: value }))
        // Clear validation error for this field
        setValidationErrors(prev => omit(prev, [name]))
    }

    useEffect(() => {
        if (data) {
            setMottaslSetting(pick(data, ["active", "apiKey", "token"]))
        }
    }, [data])

    const confirmHandler = () => {
        setValidationErrors(null)

        const errors = runJoiValidate(MOTTASL_SCHEMA, mottaslSetting)

        if (!!errors) {
            setValidationErrors(errors)
            return
        }

        onConfirm({ mottasl: mottaslSetting })
    }

    return (
        <Card sx={{ height: "100%", display: 'flex', flexDirection: 'column' }}>
            <CardHeader title={t("Mottasl")} />
            <Divider />
            <CardContent sx={{ flex: 1 }}  >
                <FormControlLabel
                    control={
                        <Switch
                            name="active"
                            checked={mottaslSetting.active}
                            onChange={({ target: { name, checked } }) => handleChange({ target: { name, value: checked } })}
                        />
                    }
                    label={t(mottaslSetting?.active ? "Enabled" : "Disabled")}
                />
                <TextField
                    fullWidth
                    label={t("API Key")}
                    value={mottaslSetting?.apiKey || ""}
                    name='apiKey'
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    margin="dense"
                    error={!!validationErrors?.apiKey}
                    helperText={validationErrors?.apiKey ?? ''}
                />
                <TextField
                    fullWidth
                    label={t("Token")}
                    value={mottaslSetting?.token || ""}
                    name='token'
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    margin="dense"
                    error={!!validationErrors?.token}
                    helperText={validationErrors?.token ?? ''}
                />
            </CardContent>
            <Divider />
            <CardActions sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexDirection: 'row-reverse' }}>
                <LoadingButton
                    variant="contained"
                    loading={loading}
                    onClick={confirmHandler}
                    sx={{ textTransform: "none" }}
                >
                    {t("Confirm")}
                </LoadingButton>
                {isSuccess && <Typography color="success.main">{t("Updated successfully")}</Typography>}
                {!!error && <Typography color="error">{t(error)}</Typography>}
            </CardActions>
        </Card>
    );
};

